import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import type { ServiceKey } from '@/lib/serviceKeys';

/**
 * Phase 3 — Frontend service access hook
 *
 * Checks whether the current org has access to a given service.
 * Fail-closed: returns `false` if the subscription or mapping cannot be verified.
 */
export function useServiceAccess(serviceKey: ServiceKey) {
  const { profile } = useAuth();
  const orgId = profile?.org_id;

  const { data: hasAccess = false, isLoading } = useQuery({
    queryKey: ['service-access', orgId, serviceKey],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('check_org_service_access', {
        _org_id: orgId!,
        _service_key: serviceKey,
      });
      if (error || data === null || data === undefined) return false;
      return !!data;
    },
    enabled: !!orgId,
    staleTime: 5 * 60 * 1000,
  });

  return { hasAccess, isLoading };
}
